import type { Branded } from './branded'
import type { EntryId } from './entry'
import type { PlanId } from './plan'
import type { ProjectId, UserId } from './project'

type IssueId = Branded<string, 'IssueId'>

const issueId = (value: string): IssueId => value as IssueId

const ISSUE_KIND = {
	TICKET: 'ticket',
} as const

const ISSUE_KINDS = [ISSUE_KIND.TICKET] as const

type IssueKind = (typeof ISSUE_KINDS)[number]

const ISSUE_STATUS = {
	OPEN: 'open',
	IN_PROGRESS: 'in_progress',
	BLOCKED: 'blocked',
	PENDING: 'pending',
	DONE: 'done',
	CANCELLED: 'cancelled',
} as const

const ISSUE_STATUSES = [
	ISSUE_STATUS.OPEN,
	ISSUE_STATUS.IN_PROGRESS,
	ISSUE_STATUS.BLOCKED,
	ISSUE_STATUS.PENDING,
	ISSUE_STATUS.DONE,
	ISSUE_STATUS.CANCELLED,
] as const

type IssueStatus = (typeof ISSUE_STATUSES)[number]

const DEFAULT_ISSUE_STATUSES: readonly IssueStatus[] = ISSUE_STATUSES.filter(
	status => status !== ISSUE_STATUS.DONE && status !== ISSUE_STATUS.CANCELLED,
)

const PRIORITY = {
	LOW: 'low',
	MEDIUM: 'medium',
	HIGH: 'high',
} as const

const PRIORITIES = [PRIORITY.LOW, PRIORITY.MEDIUM, PRIORITY.HIGH] as const

type Priority = (typeof PRIORITIES)[number]

const SIZES = [1, 2, 3, 5, 8] as const

type Size = (typeof SIZES)[number]

const WAYFINDER_TYPES = ['map', 'research', 'prototype', 'grilling', 'task'] as const

type WayfinderType = (typeof WAYFINDER_TYPES)[number]

const LINK_KIND = {
	BLOCKS: 'blocks',
	RELATES: 'relates',
	PARENT: 'parent',
} as const

const LINK_KINDS = [LINK_KIND.BLOCKS, LINK_KIND.RELATES, LINK_KIND.PARENT] as const

type LinkKind = (typeof LINK_KINDS)[number]

type IssueLink = {
	readonly from: IssueId
	readonly to: IssueId
	readonly kind: LinkKind
}

type Issue = {
	readonly id: IssueId
	readonly kind: IssueKind
	readonly projectId: ProjectId
	readonly planId: PlanId | undefined
	readonly slug: string
	readonly title: string
	readonly body: string
	readonly status: IssueStatus
	readonly priority: Priority
	readonly size: Size | undefined
	readonly assignee: UserId | undefined
	readonly tags: readonly string[]
	readonly position: number
	readonly dueDate: Date | undefined
	readonly wayfinder: WayfinderType | undefined
	readonly externalRef: string
	readonly resolution: string
	readonly resolutionEntry: EntryId | undefined
	readonly createdBy: UserId | undefined
	readonly createdAt: Date
	readonly updatedAt: Date
	readonly parentId: IssueId | undefined
	readonly childCount: number
	readonly dependsOn: readonly IssueId[]
	readonly relatedTo: readonly IssueId[]
	readonly blocked: boolean
	readonly archived: boolean
}

const isTerminal = (status: IssueStatus): boolean => status === ISSUE_STATUS.DONE || status === ISSUE_STATUS.CANCELLED

const PRIORITY_WEIGHT: Record<Priority, number> = { low: 1, medium: 2, high: 3 }

const score = (issue: Pick<Issue, 'priority' | 'size'>): number =>
	issue.size === undefined ? 0 : PRIORITY_WEIGHT[issue.priority] / issue.size

const isSize = (value: number): value is Size => (SIZES as readonly number[]).includes(value)

export {
	DEFAULT_ISSUE_STATUSES,
	isSize,
	ISSUE_KIND,
	ISSUE_KINDS,
	ISSUE_STATUS,
	ISSUE_STATUSES,
	issueId,
	isTerminal,
	LINK_KIND,
	LINK_KINDS,
	PRIORITIES,
	PRIORITY,
	score,
	SIZES,
	WAYFINDER_TYPES,
}
export type { Issue, IssueId, IssueKind, IssueLink, IssueStatus, LinkKind, Priority, Size, WayfinderType }
